import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { CalendarCheck, FolderOpen, Lock, ClipboardList, RefreshCw, UserX } from "lucide-react";

import { byDay, caseloadConfig, learnerLink, nextStep, severityTone, viewerNote, type CaseFile, type CounsellingDesk } from "../../components/counsellor/counsellorLogic";
import { DecisionsCard, DeskTiles, ViewerNote, type DeskTile } from "../../components/dashboard/DeskParts";
import { longDay, shortDay } from "../../components/dashboard/deskLogic";
import { ErrorState, LoadingState } from "../../components/PageState";
import { ClientRegister, StatusDot, type RegisterColumn } from "../../components/register/ClientRegister";
import { api } from "../../services/api";

/**
 * The school counsellor's dashboard: what needs doing, the week's
 * appointments and the caseload. It shows who, when and what kind only --
 * never what was said -- and the counsellor sees only their own work.
 */
export default function SchoolCounsellorDashboard() {
  const navigate = useNavigate();
  const query = useQuery({
    queryKey: ["counselling-desk"],
    queryFn: async () => (await api.get("/dashboard/school-counsellor/")).data as CounsellingDesk,
    refetchInterval: 60_000,
  });

  if (query.isLoading) return <LoadingState label="Loading the counsellor's dashboard…" />;
  if (query.isError || !query.data) return <ErrorState title="Dashboard unavailable" message="The counsellor's dashboard could not be loaded. Check your connection and try again." retry={() => query.refetch()} />;

  const data = query.data;
  const figures = data.figures;
  const days = byDay(data.week);

  const tiles: DeskTile[] = [
    { label: "Seen this month", value: String(figures.seen_this_month), note: "Sessions completed", link: "/counseling?view=done", icon: CalendarCheck },
    { label: "Did not come", value: String(figures.no_shows_this_month), note: "No-shows this month", link: "/counseling?view=all", icon: UserX, alert: figures.no_shows_this_month > 0 },
    { label: "Open cases", value: String(figures.open_cases), note: "Not yet resolved", link: "/counseling?tab=cases&view=open", icon: FolderOpen },
    { label: "Support plans", value: String(figures.active_plans), note: "Active plans", link: "/counseling?tab=plans&view=active", icon: ClipboardList },
  ];

  const columns: RegisterColumn<CaseFile>[] = [
    { header: "Learner", cell: (row) => <span><span className="block font-medium text-gray-900">{row.learner}</span><span className="text-xs text-gray-500">{row.admission_number}</span></span>, exportValue: (row) => row.learner },
    { header: "Class", cell: (row) => row.class_name || "—", exportValue: (row) => row.class_name },
    { header: "Open cases", cell: (row) => (row.open_cases ? <span className="tabular-nums">{row.open_cases}</span> : "—"), exportValue: (row) => row.open_cases, align: "right" },
    { header: "Most serious", cell: (row) => <StatusDot {...severityTone(row.severity)} />, exportValue: (row) => severityTone(row.severity).label },
    { header: "Support plan", cell: (row) => (row.has_plan ? (row.plan_review ? `Review ${shortDay(row.plan_review)}` : "Active") : "—"), exportValue: (row) => (row.has_plan ? row.plan_review ?? "Active" : "") },
    { header: "Last seen", cell: (row) => (row.last_session ? shortDay(row.last_session) : "Not yet"), exportValue: (row) => row.last_session ?? "" },
    { header: "Next", cell: (row) => <span className={!row.next_session && row.open_cases ? "font-medium text-amber-800" : undefined}>{nextStep(row, data.today)}</span>, exportValue: (row) => nextStep(row, data.today) },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Counselling</h1>
          <p className="mt-1 text-sm text-gray-500">{longDay(data.today)} · your appointments, follow-ups and caseload.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link to="/counseling" className="btn btn-primary">Open counselling</Link>
          <button type="button" onClick={() => query.refetch()} className="btn btn-outline gap-2">
            <RefreshCw className={`h-4 w-4 ${query.isFetching ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
      </div>

      <ViewerNote text={viewerNote(data.viewer_role)} />
      <p className="flex items-center gap-2 text-xs text-gray-500"><Lock className="h-3.5 w-3.5" /> Confidential. This page shows who and when, never what was said; notes stay in each learner's file.</p>

      <DecisionsCard decisions={data.work} title="Work waiting" where="in counselling" />
      <DeskTiles tiles={tiles} />

      <section className="card">
        <div className="card-titlebar">
          <div><h2 className="font-semibold text-gray-900">This week's appointments</h2><p className="text-xs text-gray-500">Today and the next six days</p></div>
          <Link to="/counseling?view=upcoming" className="text-xs font-medium text-primary-700 hover:underline">All sessions</Link>
        </div>
        {days.length ? (
          <div className="divide-y divide-gray-100">
            {days.map((day) => (
              <div key={day.date} className="grid gap-2 px-6 py-3 sm:grid-cols-[9rem_1fr]">
                <p className={`text-sm font-semibold ${day.date === data.today ? "text-primary-700" : "text-gray-900"}`}>{day.date === data.today ? "Today" : shortDay(day.date)}</p>
                <ul className="space-y-1.5">
                  {day.appointments.map((row) => (
                    <li key={row.id}>
                      <Link to={learnerLink(row)} className="flex flex-wrap items-baseline gap-x-3 text-sm hover:text-primary-700">
                        <span className="w-12 tabular-nums text-gray-600">{row.time}</span>
                        <span className="font-medium text-gray-900">{row.learner}</span>
                        <span className="text-xs text-gray-500">{[row.class_name, row.session_type, `${row.minutes} min`, data.viewer_role === "admin" ? row.counsellor : ""].filter(Boolean).join(" · ")}</span>
                        {row.status === "in_progress" && <span className="text-xs font-medium text-sky-700">Under way</span>}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        ) : <p className="px-6 py-6 text-center text-sm text-gray-500">No sessions booked this week.</p>}
      </section>

      <ClientRegister
        title="Caseload"
        description="One row per learner with an open case or an active support plan, most serious first. Open a learner for their sessions."
        rows={data.caseload}
        config={caseloadConfig(data.today)}
        columns={columns}
        rowKey={(row) => row.student}
        onOpen={(row) => navigate(learnerLink(row))}
        openLabel={() => "Sessions"}
        noun={["learner", "learners"]}
        canExport={false}
        exportName={{ file: "caseload", title: "Counselling caseload" }}
        emptyText="No learner has an open case or an active support plan."
      />
    </div>
  );
}
